import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  AttachmentOutlined,
  CameraAltOutlined,
  DeleteOutline,
  Download,
} from "@mui/icons-material";
import { Fade, Modal, Tooltip, Typography } from "@mui/material";

import { API_ENDPOINT } from "../../services/api/resources";
import { startExtraction, stopExtraction } from "../../features/UISlice";
import BrandLogo from "../../components/common/BrandLogo/BrandLogo";
import SuccessPopupDashboard from "../../components/Popups/PopupFrame/SuccessPopupDashboard";
import SelectCountry from "../../components/Dashboard/amount/SelectCountry";
import brandLogo from "../../assets/images/png/Logo@2x.png";
import successPopupImg from "../../assets/images/png/Group 2 2@2x.png";
import { CurrencyContext } from "../../context/CurrencyContext";
import styles from "./FreeRFQDocumentExtraction.module.css";

const specNameTooltipProps = {
  tooltip: {
    className: styles.specNameTooltip,
    sx: {
      maxWidth: "min(38rem, 88vw)",
      padding: "8px 10px",
      backgroundColor: "#101828",
      color: "#fff",
      fontFamily: "gilroySemiBold, sans-serif",
      fontSize: "14px !important",
      lineHeight: 1.45,
      wordBreak: "break-word",
      boxShadow: "0 0.8rem 2rem rgba(15, 23, 42, 0.22)",
    },
  },
  arrow: {
    className: styles.specNameTooltipArrow,
    sx: {
      color: "#101828",
    },
  },
};

const ACCEPTED_EXTENSIONS = [".jpeg", ".jpg", ".png", ".pdf", ".xls", ".xlsx", ".doc", ".docx", ".xml", ".csv"];
const ACCEPTED_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/pdf",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/xml",
  "text/xml",
  "text/csv",
];

const getFileExtension = (name = "") => `.${String(name).split(".").pop().toLowerCase()}`;
const isAcceptedUploadFile = (file) => {
  const ext = getFileExtension(file?.name || "");
  const type = String(file?.type || "").toLowerCase();
  return ACCEPTED_EXTENSIONS.includes(ext) || ACCEPTED_MIME_TYPES.includes(type);
};

const formatNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString(undefined, { maximumFractionDigits: 2 }) : "-";
};

const formatCurrency = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? `Rs. ${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "-";
};

const isBlankValue = (value) => value === null || value === undefined || value === "";

const getTotalPriceValue = (item = {}) =>
  item?.total_amount ?? item?.total_price ?? item?.totalPrice;

const getUnitPriceValue = (item = {}) => {
  const unitPrice = item?.unit_price ?? item?.unitPrice;
  return isBlankValue(unitPrice) ? getTotalPriceValue(item) : unitPrice;
};

const getQuotationItems = (quote = {}) => {
  if (Array.isArray(quote)) return quote;
  if (Array.isArray(quote?.quotation_details?.items)) return quote.quotation_details.items;
  if (Array.isArray(quote?.quotationDetails?.items)) return quote.quotationDetails.items;
  if (Array.isArray(quote?.items)) return quote.items;
  if (Array.isArray(quote?.item)) return quote.item;
  return [];
};

const getItemsTotalPrice = (items = []) =>
  items.reduce((sum, item) => sum + Number(getTotalPriceValue(item) || 0), 0);

const formatQuantityWithUnit = (item = {}) => {
  const quantity = item?.quantity ?? "";
  const unit = String(item?.unit || item?.uom || "").trim();
  const formattedQuantity =
    quantity === null || quantity === undefined || quantity === ""
      ? "-"
      : formatNumber(quantity);

  if (formattedQuantity === "-") return unit || "-";
  return unit ? `${formattedQuantity} ${unit}` : formattedQuantity;
};

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMMM YYYY") : String(value);
};

const formatTime = (value) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("hh:mm A") : String(value);
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : String(value);
};

const getInitials = (value = "") => {
  const words = String(value)
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) return "RF";
  return words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() || "")
    .join("");
};

const formatItemTax = (item = {}) => {
  const taxes = item?.GST_list || item?.gst_list || [];
  return Array.isArray(taxes)
    ? taxes
        .map((tax) => `${tax?.country || ""}${tax?.country && tax?.gst ? " - " : ""}${tax?.gst || ""}${tax?.gst ? "%" : ""}`)
        .filter(Boolean)
        .join(", ") || "-"
    : "-";
};

const buildItemSpecRows = (item = {}, context = {}) => [
  ["RFQ ID", context?.rfqCode || "-"],
  ["Buyer Company", context?.buyerCompany || "-"],
  ["Buyer Email", context?.buyerEmail || "-"],
  ["Buyer Mobile", context?.buyerMobile || "-"],
  ["Item Code", item?.itemCode || item?.item_code || "-"],
  ["Category", item?.category || "-"],
  ["Sub-category", item?.subCategory || item?.sub_category || "-"],
  ["Item Type", item?.itemType || item?.item_type || "-"],
  ["Quantity", formatQuantityWithUnit(item)],
  ["UNSPSC", item?.unspscCode || item?.unspsc_code || "-"],
  ["HSN", item?.hsnCode || item?.hsn_code || "-"],
  ["Expense Type", item?.expense_type || "-"],
  ["Unit", item?.unit_measure || item?.uom || item?.unit || "-"],
  ["Manufacturer Part No.", item?.partNumber || item?.part_number || item?.manufacturer_part_no || "-"],
  ["Brand", item?.brand || "-"],
  ["Tax", formatItemTax(item)],
  ["Aliases", item?.aliases || "-"],
  ["Specifications", item?.specifications || "-"],
  ["Description", item?.description || item?.item_description || "-"],
];

const buildQuotationSpecSections = (quoteRow = {}) => {
  const rawQuote = quoteRow?.rawQuote || {};
  const quotationDetails = rawQuote?.quotation_details || {};
  const quoteItems = getQuotationItems(rawQuote);
  const warrantyDetails =
    quotationDetails?.warranty_details ||
    quotationDetails?.warranty ||
    rawQuote?.warranty_details ||
    rawQuote?.warranty ||
    {};

  const sections = [
    {
      title: "Quotation Details",
      rows: [
        ["Supplier", quoteRow?.supplierName || rawQuote?.vendor_email || "-"],
        ["Submitted", quoteRow?.submittedAt || "-"],
        ["Line Items", quoteItems.length || 0],
        ["File Name", quoteRow?.documentName || "-"],
      ],
    },
    {
      title: "Quotation Summary",
      rows: [
        ["Currency", quotationDetails?.currency || rawQuote?.currency || "-"],
        ["Freight", formatCurrency(quotationDetails?.freight ?? rawQuote?.freight ?? 0)],
        [
          "Grand Total",
          formatCurrency(
            quotationDetails?.grand_total ??
              rawQuote?.grand_total ??
              quoteRow?.rawQuote?.grand_total ??
              getItemsTotalPrice(quoteItems),
          ),
        ],
      ],
    },
    {
      title: "Warranty Details",
      rows: [
        ["Raw Text", warrantyDetails?.warranty_raw_text || "-"],
        ["Warranty Number", warrantyDetails?.warranty_number || "-"],
        ["Warranty Period", warrantyDetails?.warranty_period || "-"],
        ["Warranty Type", warrantyDetails?.warranty_type || "-"],
      ],
    },
  ];

  const itemSection = quoteItems.length
    ? {
        title: "Item Details",
        tableHeaders: [
          "Sl No",
          "Actual Name",
          "Item Name",
          "Quantity",
          "Unit Price",
          "Tax",
          "Total Price",
          "Description",
        ],
        tableRows: quoteItems.map((item, index) => [
          index + 1,
          item?.original_item_name || item?.originalItemName || "-",
          item?.item_name || item?.itemName || "-",
          formatQuantityWithUnit(item),
          formatCurrency(getUnitPriceValue(item)),
          isBlankValue(item?.tax) ? "-" : `${formatNumber(item.tax)}%`,
          formatCurrency(getTotalPriceValue(item)),
          item?.description || "-",
        ]),
      }
    : {
        title: "Item Details",
        tableHeaders: [
          "Sl No",
          "Actual Name",
          "Item Name",
          "Quantity",
          "Unit Price",
          "Tax",
          "Total Price",
          "Description",
        ],
        tableRows: [[1, "No quotation item details available.", "-", "-", "-", "-", "-", "-"]],
      };

  return [...sections, itemSection];
};

const ReadMoreText = ({ value, limit = 90 }) => {
  const [expanded, setExpanded] = useState(false);
  const text = String(value ?? "-").trim() || "-";
  const canTruncate = text.length > limit;
  const visibleText = canTruncate && !expanded ? `${text.slice(0, limit).trim()}...` : text;

  return (
    <span className={styles.readMoreText}>
      {visibleText}
      {canTruncate ? (
        <button
          type="button"
          className={styles.readMoreButton}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? "Read less" : "Read more"}
        </button>
      ) : null}
    </span>
  );
};

const createCaptureFileName = () => `capture-${dayjs().format("YYYYMMDD-HHmmss")}.jpg`;

const getCloudFrontFileUrl = (fileUrl) => {
  const rawUrl = String(fileUrl || "").trim();
  if (!rawUrl) return "";

  const cloudFrontUrl = String(process.env.REACT_APP_CLOUDFRONT_URL || "")
    .trim()
    .replace(/\/+$/, "");

  if (!cloudFrontUrl) return rawUrl;

  if (rawUrl.startsWith("blob:") || rawUrl.startsWith("data:")) {
    return rawUrl;
  }

  if (rawUrl.startsWith(cloudFrontUrl)) {
    return rawUrl;
  }

  try {
    const parsedUrl = new URL(rawUrl);
    return `${cloudFrontUrl}${parsedUrl.pathname}${parsedUrl.search || ""}`;
  } catch (error) {
    const cleanPath = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
    return `${cloudFrontUrl}${cleanPath}`;
  }
};

const downloadFileFromUrl = async (fileUrl, fileName = "quotation-file") => {
  const url = getCloudFrontFileUrl(fileUrl);
  if (!url) return;

  const response = await axios.get(url, { responseType: "blob" });
  saveAs(response.data, fileName);
};

const normalizeAttachmentEntry = (doc, index) => {
  if (!doc) return null;

  if (typeof doc === "string") {
    return {
      url: getCloudFrontFileUrl(doc),
      name: doc.split("/").pop() || `Attachment ${index + 1}`,
    };
  }

  const url = doc.url || doc.uri || doc.document || doc.file || "";
  if (!url) return null;

  return {
    url: getCloudFrontFileUrl(url),
    name: doc.name || url.split("/").pop() || `Attachment ${index + 1}`,
  };
};

const normalizeChatAttachmentEntry = (doc, index) => {
  if (!doc) return null;

  if (typeof doc === "string") {
    return {
      url: getCloudFrontFileUrl(doc),
      name: doc.split("/").pop() || `Attachment ${index + 1}`,
    };
  }

  const url = doc.attachment_url || doc.attachment || doc.url || doc.uri || "";
  if (!url) return null;

  return {
    url: getCloudFrontFileUrl(url),
    name:
      doc.attachment_name ||
      doc.name ||
      String(url).split("/").pop() ||
      `Attachment ${index + 1}`,
  };
};



const parseTimelineDate = (value) => {
  if (!value || typeof value !== "string") return new Date(0);
  try {
    if (value.includes(" - ")) {
      const [datePart, timePart] = value.split(" - ");
      return new Date(`${datePart.trim()} ${timePart.trim()}`);
    }
    return new Date(value.trim());
  } catch (error) {
    return new Date(0);
  }
};

const toTitleCase = (value = "") =>
  String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const buildShippingAddress = (detail) =>
  [
    detail?.shipping_address1,
    detail?.shipping_address2,
    detail?.city,
    detail?.state,
    detail?.pincode,
    detail?.country,
  ]
    .filter(Boolean)
    .join(", ");

const normalizeVendorQuotationTokens = (detail) => {
  const token1 = detail?.vendor_quotation_token;
  const token2 = detail?.vendor_quotation_tokens;

  return [
    ...(Array.isArray(token1) ? token1 : token1 ? [token1] : []),
    ...(Array.isArray(token2) ? token2 : token2 ? [token2] : []),
  ]
    .map((token) => String(token || "").trim())
    .filter(Boolean);
};

const normalizeVendorQuotationTokensFromSaveResponse = (response) => {
  const payload = response?.data ?? response ?? {};

  return [
    payload?.vendor_quotation_token,
    payload?.quotation_token,
    payload?.data?.vendor_quotation_token,
    payload?.data?.quotation_token,
    ...(Array.isArray(payload?.vendor_quotation_tokens) ? payload.vendor_quotation_tokens : []),
    ...(Array.isArray(payload?.data?.vendor_quotation_tokens) ? payload.data.vendor_quotation_tokens : []),
  ]
    .map((token) => String(token || "").trim())
    .filter(Boolean);
};

const normalizeExtractResponse = (response) => {
  const root = response?.data ?? response ?? {};
  const payload =
    root?.data ??
    root?.result ??
    root?.extracted_data ??
    root?.extractedData ??
    root?.details ??
    root;

  const payloadItems = Array.isArray(payload)
    ? payload
    : Array.isArray(root)
      ? root
      : getQuotationItems(payload);
  const parsedNormalized = payload?.parsed_normalized || payload?.parsedNormalized || {};
  const parsed = payload?.parsed || {};
  const extracted = payload?.extracted || {};
  const normalizedQuotation = parsedNormalized?.items?.length
    ? parsedNormalized
    : extracted?.items?.length
      ? extracted
      : {};
  const normalizedItems =
    normalizedQuotation?.items?.length
      ? normalizedQuotation.items
      : parsed?.items?.length
        ? parsed.items
        : payloadItems;

  return {
    fileName: payload?.file_name || root?.file_name || "",
    docPageCount: payload?.doc_page_count || root?.doc_page_count || 0,
    quotation: {
      freight: normalizedQuotation?.freight ?? 0,
      warranty: normalizedQuotation?.warranty_details || normalizedQuotation?.warranty || {},
      items: normalizedItems,
      grand_total: normalizedQuotation?.grand_total ?? 0,
      currency: normalizedQuotation?.currency || "",
    },
    payload,
  };
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getExtractionStatus = (response) => {
  const root = response?.data ?? response ?? {};
  return String(
    root?.status ||
      root?.extraction_status ||
      root?.data?.status ||
      root?.data?.extraction_status ||
      "",
  )
    .trim()
    .toLowerCase();
};

const getExtractionJobToken = (response) => {
  const root = response?.data ?? response ?? {};
  return String(
    root?.job_token ||
      root?.data?.job_token ||
      "",
  ).trim();
};

const getExtractionStatusMessage = (response) => {
  const root = response?.data ?? response ?? {};
  const message = root?.message || root?.response || root?.data?.message || root?.data?.response || "";
  const progress = root?.progress ?? root?.data?.progress;

  if (message && progress !== undefined && progress !== null && progress !== "") {
    return `${message} (${progress}%)`;
  }

  return message;
};

const getExtractionStatusDetails = (response) => {
  const root = response?.data ?? response ?? {};
  const data = root?.data || {};

  return {
    status: getExtractionStatus(response),
    message: root?.message || root?.response || data?.message || data?.response || "",
    progress: root?.progress ?? data?.progress ?? 0,
    fileName: root?.file_name || data?.file_name || "",
    docPageCount: root?.doc_page_count ?? data?.doc_page_count ?? "",
    extractMode: root?.extract_mode || data?.extract_mode || "",
    jobToken: root?.job_token || data?.job_token || "",
  };
};

const isExtractionDoneStatus = (status) =>
  ["completed", "complete", "done", "success", "succeeded", "finished"].includes(status);

const isExtractionFailedStatus = (status) =>
  ["failed", "failure", "error", "cancelled", "canceled"].includes(status);

const buildManualQuotationPayload = ({
  rfqContext,
  selectedFile,
  manualBidRows,
  selectedCurrency,
  selectedExchangeRate,
}) => {
  const items = manualBidRows.map((row, index) => {
    const quantity = Number(row.quantity || 0);
    const unitPrice = Number(row.unitPrice || 0);

    return {
      line_no: index + 1,
      item_name: row.itemName || "-",
      item_code: row.itemCode || "-",
      quantity,
      unit_price: unitPrice,
      total_price: quantity * unitPrice,
      description: row.proposalDescription || "",
      hsn_code: row.hsnCode || "",
      unit: row.unit || "",
      expense_type: row.expenseType || "",
    };
  });

  return {
    rfq_token: rfqContext?.rfq_token || "",
    rfq_code: rfqContext?.rfq_code || "",
    vendor_email: rfqContext?.vendor_contacts?.[0]?.email || "",
    vendor_mobile: rfqContext?.vendor_contacts?.[0]?.mobile_number || "",
    file_name: selectedFile?.name || "",
    doc_page_count: 0,
    buyer_details: {
      buyer_company_name: rfqContext?.buyer_contact?.company_name || "",
      buyer_email: rfqContext?.buyer_contact?.email || "",
      buyer_mobile: rfqContext?.buyer_contact?.mobile_number || "",
    },
    rfq_details: {
      rfq_token: rfqContext?.rfq_token || "",
      rfq_code: rfqContext?.rfq_code || "",
    },
    quotation_details: {
      freight: 0,
      warranty_details: {},
      grand_total: items.reduce((sum, item) => sum + Number(item.total_price || 0), 0),
      currency: selectedCurrency || "INR",
      exchange_rate_to_inr: selectedExchangeRate ?? null,
      items,
    },
  };
};

const buildExtractedQuotationPayload = ({
  rfqContext,
  selectedFile,
  extractionResult,
  selectedCurrency,
  selectedExchangeRate,
}) => {
  const quotation = extractionResult?.quotation || {};
  const items = Array.isArray(quotation?.items)
    ? quotation.items.map((item) => ({
        ...item,
        unit_price: getUnitPriceValue(item),
      }))
    : [];

  return {
    rfq_token: rfqContext?.rfq_token || "",
    rfq_code: rfqContext?.rfq_code || "",
    vendor_email: rfqContext?.vendor_contacts?.[0]?.email || "",
    vendor_mobile: rfqContext?.vendor_contacts?.[0]?.mobile_number || "",
    file_name: extractionResult?.fileName || selectedFile?.name || "",
    doc_page_count: Number(extractionResult?.docPageCount || 0),
    buyer_details: {
      buyer_company_name: rfqContext?.buyer_contact?.company_name || "",
      buyer_email: rfqContext?.buyer_contact?.email || "",
      buyer_mobile: rfqContext?.buyer_contact?.mobile_number || "",
    },
    rfq_details: {
      rfq_token: rfqContext?.rfq_token || "",
      rfq_code: rfqContext?.rfq_code || "",
    },
    quotation_details: {
      freight: quotation?.freight ?? 0,
      warranty_details: quotation?.warranty || {},
      grand_total: quotation?.grand_total ?? getItemsTotalPrice(items),
      currency: quotation?.currency || selectedCurrency || "INR",
      exchange_rate_to_inr: selectedExchangeRate ?? null,
      items,
    },
  };
};

const buildSaveFormData = (payload, file) => {
  const formData = new FormData();

  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    formData.append(key, typeof value === "object" ? JSON.stringify(value) : value);
  });

  if (file) {
    formData.append("document", file);
  }

  return formData;
};

const createFreeRfqManualQuotationPdfFile = async (quote = {}) => {
  const rfqCode = quote?.rfq_code || quote?.rfq_details?.rfq_code || "RFQ";
  const vendorEmail = quote?.vendor_email || "-";
  const vendorName =
    quote?.vendor_name ||
    quote?.vendor_contact_name ||
    quote?.company_name ||
    vendorEmail;
  const currencyType = quote?.quotation_details?.currency || "INR";
  const currencyValue = quote?.quotation_details?.exchange_rate_to_inr ?? 1;
  const convertedCurrency = quote?.quotation_details?.grand_total ?? 0;
  const inrValue =
    Number(currencyValue || 0) * Number(convertedCurrency || 0) || convertedCurrency || 0;
  const quoteItems = Array.isArray(quote?.quotation_details?.items)
    ? quote.quotation_details.items
    : getQuotationItems(quote);

  const formatNumber = (value) => {
    const num = Number(String(value ?? "").replace(/,/g, "").trim());
    if (!Number.isFinite(num)) return "-";
    return num.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  };

  const safeText = (value) => String(value ?? "").trim() || "-";
  const safeRfq = String(rfqCode || "RFQ").replace(/[\\/:*?"<>|]/g, "_");
  const fileName = `${safeRfq}_QuotationSummary.pdf`;
  const nowStr = new Date(quote?.updated_at || quote?.created_at || Date.now()).toLocaleString("en-IN");

  const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const marginX = 40;

  let y = 48;
  let titleStartX = marginX;

  try {
    const logoW = 88;
    const logoH = 28;
    pdf.addImage(brandLogo, "png", marginX, 22, logoW, logoH);
    y = 46;
    titleStartX = marginX + logoW + 12;
  } catch (error) {
    // Continue with text-only PDF if logo rendering fails.
  }

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.text("Quotation Submission Details", titleStartX, y);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(71, 85, 105);
  pdf.text(`Generated On: ${nowStr}`, pageWidth - marginX, y, { align: "right" });
  pdf.setTextColor(15, 23, 42);
  y += 18;

  pdf.setDrawColor(226, 232, 240);
  pdf.line(marginX, y, pageWidth - marginX, y);
  y += 18;

  const submittedBy = vendorName
    ? `${safeText(vendorName)} <${safeText(vendorEmail)}>`
    : safeText(vendorEmail);

  pdf.autoTable({
    theme: "plain",
    startY: y,
    margin: { left: marginX, right: marginX },
    tableWidth: pageWidth - marginX * 2,
    body: [
      ["RFQ ID", safeText(rfqCode), "Currency", safeText(currencyType)],
      ["Submitted By", submittedBy, "Buyer", safeText(quote?.buyer_name || quote?.buyer_email)],
    ],
    styles: {
      fontSize: 10.5,
      cellPadding: { top: 2, right: 6, bottom: 2, left: 0 },
      overflow: "linebreak",
    },
    columnStyles: {
      0: { cellWidth: 78, textColor: [71, 85, 105], fontStyle: "bold" },
      1: { cellWidth: 210, textColor: [15, 23, 42] },
      2: { cellWidth: 78, textColor: [71, 85, 105], fontStyle: "bold" },
      3: { cellWidth: "auto", textColor: [15, 23, 42] },
    },
  });

  y = (pdf.lastAutoTable?.finalY || y + 40) + 14;

  const head = [[
    "Item Code",
    "Item Name",
    "Quantity",
    "Unit Price",
    "Total Price",
    "Description",
  ]];
  const body = quoteItems.length
    ? quoteItems.map((item) => [
        safeText(item?.item_code || item?.itemCode),
        safeText(item?.item_name || item?.itemName),
        formatQuantityWithUnit(item),
        currencyType ? `${safeText(currencyType)} ${formatNumber(item?.unit_price || item?.unitPrice)}` : formatNumber(item?.unit_price || item?.unitPrice),
        currencyType ? `${safeText(currencyType)} ${formatNumber(item?.total_price || item?.totalPrice)}` : formatNumber(item?.total_price || item?.totalPrice),
        safeText(item?.description),
      ])
    : [["-", "-", "-", "-", "-", "-"]];

  pdf.autoTable({
    theme: "grid",
    startY: y,
    head,
    body,
    margin: { left: marginX, right: marginX },
    headStyles: {
      fillColor: [59, 134, 199],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "center",
    },
    styles: {
      fontSize: 9,
      cellPadding: 4,
      overflow: "linebreak",
      valign: "top",
    },
    columnStyles: {
      0: { cellWidth: 62 },
      1: { cellWidth: 118 },
      2: { cellWidth: 62, halign: "right" },
      3: { cellWidth: 68, halign: "right" },
      4: { cellWidth: 72, halign: "right" },
      5: { cellWidth: "auto" },
    },
  });

  const footerY = (pdf.lastAutoTable?.finalY || y + 120) + 14;
  pdf.autoTable({
    theme: "grid",
    startY: footerY,
    margin: { left: marginX, right: marginX },
    head: [["Currency Value", "Converted", "INR"]],
    body: [[formatNumber(currencyValue), formatNumber(convertedCurrency), formatNumber(inrValue)]],
    headStyles: {
      fillColor: [241, 245, 249],
      textColor: [71, 85, 105],
      fontStyle: "bold",
      halign: "center",
    },
    styles: { fontSize: 9.5, cellPadding: 4, halign: "center", overflow: "linebreak" },
    columnStyles: {
      0: { cellWidth: (pageWidth - marginX * 2) / 3 },
      1: { cellWidth: (pageWidth - marginX * 2) / 3 },
      2: { cellWidth: (pageWidth - marginX * 2) / 3 },
    },
  });

  const blob = pdf.output("blob");
  return new File([blob], fileName, { type: "application/pdf" });
};

const FreeRFQDocumentExtraction = () => {
  const { fromCurrency, setFromCurrency } = useContext(CurrencyContext);
  const dispatch = useDispatch();
  const loginData = useSelector((state) => state.loginData?.value || {});
  const location = useLocation();
  const authToken = loginData?.auth_token || loginData?.token || "";
  const userToken = loginData?.user?.token || "";
  const orgToken =
    loginData?.organization_token || loginData?.org_token || loginData?.user?.organization_token || userToken;
  const userRole =
    loginData?.user?.role ||
    loginData?.access_type ||
    loginData?.user?.access_type ||
    "vendor contact";

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [rfqContext, setRfqContext] = useState(null);
  const [vendorQuotationContext, setVendorQuotationContext] = useState([]);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [timelineModalOpen, setTimelineModalOpen] = useState(false);
  const [descriptionModal, setDescriptionModal] = useState({
    open: false,
    title: "",
    rows: [],
    sections: [],
  });
  const [messageText, setMessageText] = useState("");
  const [messageFiles, setMessageFiles] = useState([]);
  const [proposalMode, setProposalMode] = useState("yes");
  const [proposalFile, setProposalFile] = useState(null);
  const [manualBidRows, setManualBidRows] = useState([]);
  const [sendMessageError, setSendMessageError] = useState("");
  const [submitBidError, setSubmitBidError] = useState("");
  const [showCustomLoader, setShowCustomLoader] = useState(false);
  const [extractionStep, setExtractionStep] = useState("initial");
  const [extractionStatusMessage, setExtractionStatusMessage] = useState("");
  const [extractionStatusDetails, setExtractionStatusDetails] = useState({
    status: "initial",
    message: "Preparing quotation extraction...",
    progress: 0,
    fileName: "",
    docPageCount: "",
    extractMode: "",
    jobToken: "",
  });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [submittingBid, setSubmittingBid] = useState(false);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState("");
  const [timelineData, setTimelineData] = useState([]);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [cameraLoading, setCameraLoading] = useState(false);
  const [currencyRate, setCurrencyRate] = useState(null);
  const [infoPopup, setInfoPopup] = useState({ open: false, title: "", description: "" });
  const [responseModal, setResponseModal] = useState({
    open: false,
    title: "",
    message: "",
  });
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const actionRowRef = useRef(null);
const [actionScrollState, setActionScrollState] = useState({
  left: false,
  right: false,
});

const extractionStepMessages = {
  initial: "Preparing quotation extraction...",
  queued: "Queued for quotation extraction...",
  uploading: "Uploading quotation document...",
  extracting_text: "Extracting text from quotation...",
  processing: "Processing quotation with AI...",
  almost_done: "Finalizing extracted quotation data...",
  parsing: "Parsing quotation details...",
  validating: "Validating extracted line items...",
  saving: "Saving quotation...",
  completed: "Quotation extracted successfully.",
  failed: "Quotation extraction failed.",
};

const extractionStepColors = {
  initial: "#101828",
  queued: "#475467",
  uploading: "#2563eb",
  extracting_text: "#7c3aed",
  processing: "#0f766e",
  almost_done: "#b45309",
  parsing: "#0f766e",
  validating: "#b45309",
  saving: "#1d4ed8",
  completed: "#15803d",
  failed: "#b42318",
};

  const fetchQuotationExtractionResult = async (jobToken) => {
    if (!API_ENDPOINT.get_free_rfq_vendor_quotation_extraction) {
      throw new Error("Quotation extraction status API is not configured.");
    }

    let latestResponse = null;

    for (let attempt = 0; attempt < 30; attempt += 1) {
      if (attempt > 0) {
        await wait(Math.min(1500 + attempt * 250, 5000));
      }

      latestResponse = await axios.post(API_ENDPOINT.get_free_rfq_vendor_quotation_extraction, {
        job_token: jobToken,
      });

      const status = getExtractionStatus(latestResponse);
      const statusMessage = getExtractionStatusMessage(latestResponse);
      const statusDetails = getExtractionStatusDetails(latestResponse);

      setExtractionStatusDetails((prev) => ({
        ...prev,
        ...statusDetails,
        status: statusDetails.status || prev.status,
        message: statusDetails.message || prev.message,
        jobToken: statusDetails.jobToken || prev.jobToken,
        fileName: statusDetails.fileName || prev.fileName,
        docPageCount: statusDetails.docPageCount || prev.docPageCount,
        extractMode: statusDetails.extractMode || prev.extractMode,
      }));

      if (statusMessage) {
        setExtractionStatusMessage(statusMessage);
      }

      if (!status || status === "queued" || status === "pending") {
        setExtractionStep("queued");
        continue;
      }

      if (status === "extracting" || status === "extracting_text") {
        setExtractionStep("extracting_text");
        continue;
      }

      if (status === "processing") {
        setExtractionStep("processing");
        continue;
      }

      if (status === "almost_done") {
        setExtractionStep("almost_done");
        continue;
      }

      if (isExtractionDoneStatus(status)) {
        setExtractionStep("completed");
        return latestResponse;
      }

      if (isExtractionFailedStatus(status)) {
        setExtractionStep("failed");
        throw new Error(
          latestResponse?.data?.message ||
            latestResponse?.data?.error ||
            "Quotation extraction failed.",
        );
      }

      setExtractionStep("extracting_text");
    }

    return latestResponse;
  };

  const extractVendorQuotationDocument = async (formData) => {
    if (!API_ENDPOINT.start_free_rfq_vendor_quotation_extraction) {
      throw new Error("Quotation extraction upload API is not configured.");
    }

    setExtractionStep("uploading");

    const startResponse = await axios.post(
      API_ENDPOINT.start_free_rfq_vendor_quotation_extraction,
      formData,
    );
    const jobToken = getExtractionJobToken(startResponse);
    const status = getExtractionStatus(startResponse);
    const statusMessage = getExtractionStatusMessage(startResponse);
    const statusDetails = getExtractionStatusDetails(startResponse);

    setExtractionStatusDetails((prev) => ({
      ...prev,
      ...statusDetails,
      status: statusDetails.status || status || prev.status,
      message: statusDetails.message || prev.message,
      jobToken: statusDetails.jobToken || jobToken || prev.jobToken,
      fileName: statusDetails.fileName || prev.fileName,
      docPageCount: statusDetails.docPageCount || prev.docPageCount,
      extractMode: statusDetails.extractMode || prev.extractMode,
    }));

    if (statusMessage) {
      setExtractionStatusMessage(statusMessage);
    }

    if (status === "queued" || status === "pending" || jobToken) {
      setExtractionStep("queued");
    }

    if (!jobToken) {
      throw new Error("Quotation extraction async API did not return a job token.");
    }

    return fetchQuotationExtractionResult(jobToken);
  };

useEffect(() => {
  const el = actionRowRef.current;
  if (!el) return;

  const updateScrollIndicators = () => {
    setActionScrollState({
      left: el.scrollLeft > 4,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  };

  updateScrollIndicators();

  el.addEventListener("scroll", updateScrollIndicators);
  window.addEventListener("resize", updateScrollIndicators);

  return () => {
    el.removeEventListener("scroll", updateScrollIndicators);
    window.removeEventListener("resize", updateScrollIndicators);
  };
}, [rfqContext]);

  const urlRfqToken = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return String(params.get("rfq_token") || "").trim();
  }, [location.search]);

  const shortCode = useMemo(() => {
    // HashRouter exposes the query after #/easy-rfq through location.search.
    const query = (location.search || "").replace(/^\?/, "");
    return /^[a-zA-Z0-9]{6}$/.test(query) ? query : "";
  }, [location.search]);

  const participantToken = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return String(params.get("participant_token") || "").trim();
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;
    const loadFreeRfqContext = async () => {
      setLoadError("");
      setRfqContext(null);
      if (!urlRfqToken && !shortCode) {
        setLoadError("This RFQ link is invalid or incomplete.");
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        if (shortCode) {
          const response = await axios.get(
            `${API_ENDPOINT.resolve_free_rfq_short_link}/${encodeURIComponent(shortCode)}`,
          );
          if (cancelled) return;
          const target = new URL(response?.data?.url);
          if (!["https:", "http:"].includes(target.protocol)) {
            throw new Error("Invalid RFQ redirect URL.");
          }
          window.location.replace(target.href);
          return;
        }
        const response = await axios.post(
          API_ENDPOINT.get_free_rfq_detail,
          { rfq_token: urlRfqToken },
          authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : undefined,
        );

        if (cancelled) return;
        const detail = response?.data?.data || response?.data || {};
        setRfqContext(detail);
        
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load easyRFQ context:", error);
        setRfqContext(null);
        setLoadError(
          shortCode
            ? "Unable to open this RFQ short link. Please try again or ask the buyer for a new link."
            : "Unable to load this RFQ. Please try again or check the link with the buyer.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadFreeRfqContext();
    return () => { cancelled = true; };
  }, [authToken, urlRfqToken, shortCode]);

  useEffect(() => {
    const loadVendorQuotationContext = async () => {
      const tokens = normalizeVendorQuotationTokens(rfqContext);
      if (!tokens.length) {
        setVendorQuotationContext([]);
        return;
      }

      try {
        const responses = await Promise.all(
          tokens.map((vendorQuotationToken) =>
            axios.post(API_ENDPOINT.get_free_rfq_vendor_quotation_link, {
              vendor_quotation_token: vendorQuotationToken,
            }),
          ),
        );

        const combined = responses.flatMap((response) => {
          if (Array.isArray(response?.data?.results)) return response.data.results;
          if (response?.data?.vendor_quotation_token) return [response.data];
          return [];
        });

        setVendorQuotationContext(combined);
      } catch (error) {
        console.error("Failed to load easyRFQ vendor quotations:", error);
        setVendorQuotationContext([]);
      }
    };

    loadVendorQuotationContext();
  }, [rfqContext]);

  const refreshFreeRfqContext = async () => {
    if (!urlRfqToken) return null;

    const response = await axios.post(
      API_ENDPOINT.get_free_rfq_detail,
      { rfq_token: urlRfqToken },
      authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : undefined,
    );

    const detail = response?.data?.data || response?.data || {};
    setRfqContext(detail || null);
    return detail || null;
  };

  useEffect(() => {
    const nextRows = Array.isArray(rfqContext?.items)
      ? rfqContext.items.map((item) => ({
          itemName: item?.itemName || item?.item_name || "",
          itemCode: item?.itemCode || item?.item_code || "",
          hsnCode: item?.hsnCode || item?.hsn_code || "",
          unit: item?.unit || "",
          expenseType: item?.expense_type || "",
          quantity: item?.quantity || "",
          unitPrice: "",
          proposalDescription: "",
        }))
      : [];

    setManualBidRows(nextRows);
  }, [rfqContext]);

  useEffect(() => {
    const selectedCurrencyCode = String(fromCurrency?.currency || "INR").trim();
    if (!submitModalOpen || !selectedCurrencyCode) return;

    let isMounted = true;

    const fetchCurrencyRate = async () => {
      try {
        const response = await axios.get(
          `https://api.exchangerate-api.com/v4/latest/${selectedCurrencyCode}`,
        );

        if (isMounted) {
          setCurrencyRate(response?.data?.rates?.INR ?? 1);
        }
      } catch (error) {
        if (isMounted) {
          setCurrencyRate(null);
        }
        console.log("Failed to load easyRFQ currency rate:", error);
      }
    };

    fetchCurrencyRate();

    return () => {
      isMounted = false;
    };
  }, [fromCurrency?.currency, submitModalOpen]);

  

  const shippingAddress = useMemo(() => buildShippingAddress(rfqContext), [rfqContext]);

  const rfqItems = useMemo(
    () => (Array.isArray(rfqContext?.items) ? rfqContext.items : []),
    [rfqContext?.items],
  );

  const buyerCompany = rfqContext?.buyer_contact?.company_name || "-";
  const buyerEmail = rfqContext?.buyer_contact?.email || "-";
  const buyerMobile = rfqContext?.buyer_contact?.mobile_number || "-";
  const bidType = toTitleCase(rfqContext?.invite_status || rfqContext?.bid_status || "active");
const bidStartMoment = useMemo(() => {
  const parsed = dayjs(rfqContext?.bid_from_date || rfqContext?.invite_valid_from);
  return parsed.isValid() ? parsed : null;
}, [rfqContext?.bid_from_date, rfqContext?.invite_valid_from]);

const bidEndMoment = useMemo(() => {
  const parsed = dayjs(rfqContext?.bid_to_date || rfqContext?.invite_valid_to);
  return parsed.isValid() ? parsed : null;
}, [rfqContext?.bid_to_date, rfqContext?.invite_valid_to]);
  const bidProgressPercent = useMemo(() => {
    if (!bidStartMoment || !bidEndMoment) return 0;

    const start = bidStartMoment.valueOf();
    const end = bidEndMoment.valueOf();
    const now = Date.now();

    if (end <= start) return now >= end ? 100 : 0;
    if (now <= start) return 0;
    if (now >= end) return 100;

    return Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
  }, [bidEndMoment, bidStartMoment]);

  const bidWindowLabel = useMemo(() => {
    if (!bidEndMoment) return "active";

    const minutesLeft = bidEndMoment.diff(dayjs(), "minute");
    if (minutesLeft <= 0) return "closed";
    if (minutesLeft <= 24 * 60) return "closes soon";
    return "active";
  }, [bidEndMoment]);

  

  const transactionDetails = useMemo(
    () => [
      ["RFQ ID", rfqContext?.rfq_code || "-"],
      
       ["Buyer Company", buyerCompany],
    ["Buyer Email", buyerEmail],
    ["Buyer Mobile", buyerMobile],
      ["Bid Type", bidType],
      ["Proposal Document", rfqContext?.proposal_document ? "Yes" : "No"],
      
    ],
    [bidType, buyerCompany, buyerEmail, buyerMobile, rfqContext],
  );

  const quotationRows = useMemo(() => {
    return vendorQuotationContext.map((quote, quoteIndex) => {
      const quoteItems = getQuotationItems(quote);
      const documentUrl =
        quote?.document ||
        quote?.document_url ||
        quote?.quotation_document ||
        quote?.file_url ||
        quote?.quotation_details?.document ||
        "";
        const documentName =
        quote?.file_name ||
        (typeof documentUrl === "string" && documentUrl ? documentUrl.split("/").pop() : "") ||
        "";
      const submittedAt = quote?.updated_at || quote?.created_at || "";
      const supplierName =
        quote?.vendor_name ||
        quote?.vendor_contact_name ||
        quote?.company_name ||
        quote?.vendor_email ||
        rfqContext?.vendor_contacts?.[0]?.email ||
        `Supplier ${quoteIndex + 1}`;
      const totalQuantity = quoteItems.reduce((sum, item) => sum + Number(item?.quantity || 0), 0);
      const totalPriceValue = quoteItems.reduce(
        (sum, item) => sum + Number(item?.total_amount ?? item?.total_price ?? item?.totalPrice ?? 0),
        0,
      );
      const unitPriceTotal = quoteItems.reduce(
        (sum, item) => sum + Number(getUnitPriceValue(item) ?? 0),
        0,
      );
      const avgUnitPrice = quoteItems.length ? unitPriceTotal / quoteItems.length : 0;
      const itemName =
        quoteItems.length === 0
          ? "-"
          : quoteItems.length === 1
            ? quoteItems[0]?.item_name || quoteItems[0]?.itemName || "-"
            : "Multiple Items";
      const itemNames = quoteItems.map((item) => item?.item_name || item?.itemName || "-");
      const quantityLines = quoteItems.map((item) => ({
        itemName: item?.item_name || item?.itemName || "-",
        quantity: formatQuantityWithUnit(item),
      }));
      const singleQuantity = quoteItems.length === 1 ? formatQuantityWithUnit(quoteItems[0]) : "";

      return {
        id: `${quote?.vendor_quotation_token || quoteIndex}`,
        supplierName,
        supplierId: quote?.vendor_code || quote?.supplier_id || "-",
        itemName,
        itemNames,
        lineItemCount: quoteItems.length,
        primaryItemName: quoteItems[0]?.item_name || quoteItems[0]?.itemName || "-",
        quantity: singleQuantity || totalQuantity || "-",
        quantityLines,
        unitPrice: quoteItems.length ? formatCurrency(avgUnitPrice) : "-",
        totalPrice: totalPriceValue ? formatCurrency(totalPriceValue) : "-",
        submittedAt: formatDateTime(submittedAt),
        documentUrl: getCloudFrontFileUrl(documentUrl),
        documentName,
        manualPdfAvailable: !documentUrl && quoteItems.length > 0,
        rawQuote: quote,
      };
    });
  }, [rfqContext, vendorQuotationContext]);

  const timelineDocuments = useMemo(() => {
    const documents = [];

    (Array.isArray(timelineData) ? timelineData : []).forEach((entry) => {
      const docList = Array.isArray(entry?.document) ? entry.document : [];
      docList.forEach((doc, index) => {
        const normalized = normalizeChatAttachmentEntry(doc, index);
        if (normalized) {
          documents.push({ document: [normalized] });
        }
      });
    });

    return documents;
  }, [timelineData]);

  const closeInfoPopup = () => setInfoPopup({ open: false, title: "", description: "" });

  const handleManualQuotationPdfDownload = async (quoteRow) => {
    try {
      const pdfFile = await createFreeRfqManualQuotationPdfFile(quoteRow?.rawQuote || {});
      saveAs(pdfFile, pdfFile.name);
    } catch (error) {
      console.error("Failed to generate manual quotation PDF:", error);
      setResponseModal({
        open: true,
        title: "Download failed",
        message: "Unable to generate the quotation PDF right now. Please try again.",
      });
    }
  };

  const openInfoPopup = (title, description) => {
    setInfoPopup({
      open: true,
      title,
      description,
    });
  };

  const resetMessageModal = () => {
    setMessageText("");
    setMessageFiles([]);
    setSendMessageError("");
    setMessageModalOpen(false);
  };

  const stopCameraStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const closeCameraModal = () => {
    stopCameraStream();
    setCameraModalOpen(false);
    setCameraError("");
    setCameraLoading(false);
  };

  const resetSubmitModal = () => {
    setProposalMode("yes");
    setProposalFile(null);
    setSubmitBidError("");
    setSubmitModalOpen(false);
  };

  const handleMessageFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setMessageFiles((prev) => [...prev, file]);
    event.target.value = "";
  };

  const openCameraModal = async () => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      setCameraError("Camera access is not supported in this browser.");
      setCameraModalOpen(true);
      return;
    }

    setCameraLoading(true);
    setCameraError("");
    setCameraModalOpen(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (error) {
      console.error("Unable to access camera:", error);
      setCameraError("Unable to access camera. Please allow camera permission and try again.");
    } finally {
      setCameraLoading(false);
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) {
      setCameraError("Camera is not ready yet.");
      return;
    }

    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    if (!context) {
      setCameraError("Unable to capture image from camera.");
      return;
    }

    context.drawImage(video, 0, 0, width, height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setCameraError("Unable to capture image from camera.");
          return;
        }

        const capturedFile = new File([blob], createCaptureFileName(), {
          type: "image/jpeg",
        });

        setSubmitBidError("");
        setProposalFile(capturedFile);

        closeCameraModal();
      },
      "image/jpeg",
      0.92,
    );
  };

  const handleBidFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isAcceptedUploadFile(file)) {
      setSubmitBidError("Please upload a jpeg, png, pdf, xls, xlsx, doc, docx, xml, or csv file.");
      event.target.value = "";
      return;
    }

    setSubmitBidError("");
    setProposalFile(file);
    event.target.value = "";
  };

  const handleManualBidChange = (index, field, value) => {
    setManualBidRows((prev) =>
      prev.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)),
    );
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) {
      setSendMessageError("Please enter your message.");
      return;
    }

    if (!participantToken) {
      openInfoPopup(
        "Chat Not Available Yet",
        "Participant token is missing from the vendor link, so the chat message cannot be sent.",
      );
      return;
    }

    setSendingMessage(true);
    setSendMessageError("");

    try {
      const hasAttachment = messageFiles.length > 0;
      const payload = hasAttachment
        ? new FormData()
        : {
            rfq_token: rfqContext?.rfq_token || "",
            participant_token: participantToken,
            message: messageText.trim(),
          };

      if (hasAttachment) {
        payload.append("rfq_token", rfqContext?.rfq_token || "");
        payload.append("participant_token", participantToken);
        payload.append("message", messageText.trim());
        messageFiles.forEach((file) => {
          payload.append("attachment", file);
        });
      }

      const response = await axios.post(
        API_ENDPOINT.send_free_rfq_vendor_chat_message,
        payload,
        hasAttachment ? { headers: { "Content-Type": "multipart/form-data" } } : undefined,
      );

      resetMessageModal();
      await fetchTimelineData();
      openInfoPopup("Message Sent Successfully", response?.data?.response || "Message sent.");
    } catch (error) {
      console.error("Error sending vendor message:", error);
      openInfoPopup(
        "Unable To Send Message",
        (error?.response?.data && typeof error.response.data === "object"
          ? Object.values(error.response.data)[0]
          : null) ||
          error?.message ||
          "Please try again.",
      );
    } finally {
      setSendingMessage(false);
    }
  };

  const buildTimelineMessages = (rows) => {
    const list = Array.isArray(rows) ? rows : [];
    return list
      .map((msg) => ({
        id: msg?.message_token || msg?.token || msg?.id || `${msg?.created_at || ""}-${msg?.message || ""}`,
        message: msg?.message,
        sender_role: msg?.sender_type === "vendor" ? "vendor contact" : "buyer",
        receiver_role: msg?.sender_type === "vendor" ? "buyer" : "vendor contact",
        created: parseTimelineDate(msg?.created_at || msg?.created_date || msg?.created || ""),
        created_raw: formatDateTime(msg?.created_at || msg?.created_date || msg?.created || ""),
        document: msg?.attachment || msg?.attachment_url
          ? [
              {
                attachment_url: msg?.attachment_url || msg?.attachment,
                attachment_name: msg?.attachment_name || msg?.attachment,
              },
            ]
          : Array.isArray(msg?.document)
            ? msg.document
            : [],
      }))
      .sort((a, b) => a.created - b.created);
  };

  const fetchTimelineData = async () => {
    if (!participantToken) {
      setTimelineError(
        "Participant token is missing from the vendor link, so chat history cannot be loaded.",
      );
      setTimelineData([]);
      return;
    }

    setTimelineLoading(true);
    setTimelineError("");

    try {
      const response = await axios.post(
        API_ENDPOINT.get_free_rfq_vendor_chat_messages,
        {
          rfq_token: rfqContext?.rfq_token || "",
          participant_token: participantToken,
          page: 1,
          page_size: 50,
        },
      );

      setTimelineData(buildTimelineMessages(response?.data?.data?.messages || []));
    } catch (error) {
      console.error("Error fetching timeline data:", error);
      setTimelineError(
        (error?.response?.data && typeof error.response.data === "object"
          ? Object.values(error.response.data)[0]
          : null) ||
          error?.message ||
          "Something went wrong while loading the vendor chat.",
      );
      setTimelineData([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  const openTimelineModal = async () => {
    setTimelineModalOpen(true);
    await fetchTimelineData();
  };

  useEffect(() => () => stopCameraStream(), []);

  const renderHoverList = (entries, type) => {
    if (!Array.isArray(entries) || entries.length <= 1) return null;

    return (
      <div className={styles.hoverPopover}>
        {entries.map((entry, index) => (
          <div key={`${type}-${index}`} className={styles.hoverPopoverRow}>
            {type === "quantity" ? `${entry.itemName}: ${entry.quantity}` : entry}
          </div>
        ))}
      </div>
    );
  };

  const openSummaryTooltip = (event, label, entries, type) => {
    if (!Array.isArray(entries) || entries.length <= 1) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setActiveTooltip({
      label,
      entries,
      type,
      top: rect.bottom + 10,
      left: rect.left,
    });
  };

  const closeSummaryTooltip = () => {
    setActiveTooltip(null);
  };

  const renderSummaryLink = (label, entries, type) => {
    const hasMultiple = Array.isArray(entries) && entries.length > 1;
    if (!hasMultiple) {
      return <span className={styles.hoverSummaryPlain}>{label}</span>;
    }

    return (
      <button
        type="button"
        className={styles.summaryLinkButton}
        onMouseEnter={(event) => openSummaryTooltip(event, label, entries, type)}
        onMouseLeave={closeSummaryTooltip}
        onFocus={(event) => openSummaryTooltip(event, label, entries, type)}
        onBlur={closeSummaryTooltip}
      >
        {label}
      </button>
    );
  };

 const renderTimelineCards = (entries) => {
  const items = Array.isArray(entries) ? entries : [];

  if (!items.length) {
    return <div className={styles.emptyTimeline}>No vendor chat messages found for this easyRFQ.</div>;
  }

  return (
    <div className={styles.timelineCardList}>
      {items.map((item) => {
        const isSupplier =
          item?.sender_role?.toLowerCase() === "vendor admin" ||
          item?.sender_role?.toLowerCase() === "vendor contact";

        const roleLabel = isSupplier ? "Supplier" : "Buyer";

        return (
          <div
            key={item.id}
            className={`${styles.timelineCard} ${
              isSupplier ? styles.supplierChatCard : styles.buyerChatCard
            }`}
          >
            <div className={styles.timelineCardHeader}>
              <div
                className={`${styles.timelineRole} ${
                  isSupplier ? styles.supplierRole : styles.buyerRole
                }`}
              >
                {roleLabel}
              </div>

              <div className={styles.timelineDate}>{item.created_raw || "-"}</div>
            </div>

            <div className={styles.timelineMessage}>{item.message || "-"}</div>

           {Array.isArray(item.document) && item.document.length > 0 ? (
  <div className={styles.timelineAttachmentList}>
    {item.document.map((doc, index) => {
      const normalized = normalizeChatAttachmentEntry(doc, index);
      if (!normalized?.url) return null;

      return (
        <a
          key={`${normalized.url}-${index}`}
          href={normalized.url}
          target="_blank"
          rel="noreferrer"
          className={styles.timelineAttachment}
          title={normalized.url}
        >
          {normalized.name}
        </a>
      );
    })}
  </div>
) : null}
          </div>
        );
      })}
    </div>
  );
};

  const handleSubmitBid = async () => {
    if (!rfqContext?.rfq_token) {
      setSubmitBidError("RFQ token is missing.");
      return;
    }

    if (proposalMode === "yes" && !proposalFile) {
      setSubmitBidError("Please upload the proposal document.");
      return;
    }

    if (
      proposalMode === "no" &&
      manualBidRows.some(
        (row) => !String(row.quantity || "").trim() || !String(row.unitPrice || "").trim(),
      )
    ) {
      setSubmitBidError("Please enter quantity and unit price for all items.");
      return;
    }

    setSubmittingBid(true);
    setSubmitBidError("");
    if (proposalMode === "yes") {
      setShowCustomLoader(true);
      setExtractionStep("initial");
      setExtractionStatusMessage("");
      setExtractionStatusDetails({
        status: "initial",
        message: "Preparing quotation extraction...",
        progress: 0,
        fileName: proposalFile?.name || "",
        docPageCount: "",
        extractMode: "",
        jobToken: "",
      });
      dispatch(startExtraction());
    }

    try {
      let saveResponse = null;

      if (proposalMode === "yes") {
        const formData = new FormData();
        formData.append("rfq_token", rfqContext?.rfq_token || "");
        formData.append("org_token", orgToken);
        formData.append("document", proposalFile);

        const extractResponse = await extractVendorQuotationDocument(formData);

        setExtractionStep("parsing");
        const normalized = normalizeExtractResponse(extractResponse);
        setExtractionStep("validating");
        const payload = buildExtractedQuotationPayload({
          rfqContext,
          selectedFile: proposalFile,
          extractionResult: normalized,
          selectedCurrency: fromCurrency?.currency,
          selectedExchangeRate: currencyRate,
        });

        const saveFormData = buildSaveFormData(payload, proposalFile);

        setExtractionStep("saving");
        saveResponse = await axios.post(API_ENDPOINT.save_free_rfq_vendor_quotation, saveFormData);
        setExtractionStep("completed");
      } else {
        const payload = buildManualQuotationPayload({
          rfqContext,
          selectedFile: proposalFile,
          manualBidRows,
          selectedCurrency: fromCurrency?.currency,
          selectedExchangeRate: currencyRate,
        });

        const saveFormData = buildSaveFormData(payload, proposalFile);

        saveResponse = await axios.post(API_ENDPOINT.save_free_rfq_vendor_quotation, saveFormData);
      }

      resetSubmitModal();
      setResponseModal({
        open: true,
        title: "Submitted Successfully",
        message: "Quotation submitted successfully.",
      });

      const refreshedDetail = await refreshFreeRfqContext();
      const refreshedResponses = [
        ...normalizeVendorQuotationTokens(refreshedDetail),
        ...normalizeVendorQuotationTokensFromSaveResponse(saveResponse),
      ].filter((token, index, arr) => token && arr.indexOf(token) === index);

      if (!normalizeVendorQuotationTokens(refreshedDetail).length && refreshedResponses.length) {
        const responses = await Promise.all(
          refreshedResponses.map((vendorQuotationToken) =>
            axios.post(API_ENDPOINT.get_free_rfq_vendor_quotation_link, {
              vendor_quotation_token: vendorQuotationToken,
            }),
          ),
        );

        const combined = responses.flatMap((response) => {
          if (Array.isArray(response?.data?.results)) return response.data.results;
          if (response?.data?.vendor_quotation_token) return [response.data];
          return [];
        });

        setVendorQuotationContext(combined);
      }
    } catch (error) {
      console.error("Error submitting bid:", error);
      setSubmitBidError(
        (error?.response?.data && typeof error.response.data === "object"
          ? Object.values(error.response.data)[0]
          : null) ||
          error?.message ||
          "Unable to submit bid.",
      );
    } finally {
      if (proposalMode === "yes") {
        dispatch(stopExtraction());
        setShowCustomLoader(false);
        setExtractionStep("");
        setExtractionStatusMessage("");
        setExtractionStatusDetails({
          status: "initial",
          message: "Preparing quotation extraction...",
          progress: 0,
          fileName: "",
          docPageCount: "",
          extractMode: "",
          jobToken: "",
        });
      }
      setSubmittingBid(false);
    }
  };

  const summaryRows = useMemo(
  () => [
    ["RFQ ID", rfqContext?.rfq_code || "-"],
    ["Buyer Company", buyerCompany],
    ["Bid Type", bidType],
    ["Buyer Email", buyerEmail],
    ["Buyer Mobile", buyerMobile],
  ],
  [rfqContext?.rfq_code, buyerCompany, bidType, buyerEmail, buyerMobile],
);

  return (
    <div className={styles.pageShell}>
      <Modal open={showCustomLoader} hideBackdrop>
        <Fade in={showCustomLoader}>
          <div className={styles.extractionStatusModal}>
            <div className={styles.extractionStatusCard}>
              <div className={styles.extractionStatusIcon}>✓</div>
              <p className={styles.extractionStatusEyebrow}>Quotation Extraction</p>
              <h3 className={styles.extractionStatusTitle}>
                {extractionStatusDetails.message ||
                  extractionStatusMessage ||
                  extractionStepMessages[extractionStep] ||
                  "Processing quotation..."}
              </h3>
            </div>
          </div>
        </Fade>
      </Modal>

      <header className={styles.topBar}>
        <div className={styles.brandGroup}>
          <BrandLogo imgSrc={brandLogo} imgAlt="bidz.ai" />
        </div>

        <div className={styles.metaChip}>
          <span className={styles.metaLabel}>Date</span>
          <span className={styles.metaValue}>{dayjs().format("DD MMM YYYY")}</span>
        </div>
      </header>

      <main className={styles.content}>
          <p className={styles.kicker}>easyRFQ</p>
          <p className={styles.subtitle}>Supplier bid submission</p>
        {/* <section className={styles.heroCard}>
        </section> */}

        <section className={styles.mainCard}>
          {loading ? (
            <Typography sx={{ fontFamily: "gilroyMedium", fontSize: 15 }}>Loading...</Typography>
          ) : loadError ? (
            <Typography role="alert" sx={{ fontFamily: "gilroyMedium", fontSize: 15 }}>{loadError}</Typography>
          ) : !rfqContext ? (
            <Typography sx={{ fontFamily: "gilroyMedium", fontSize: 15 }}>No RFQ found.</Typography>
          ) : (
            <>
              <div className={styles.mobileHeroCard}>
                <div className={styles.mobileHeroHeader}>
                  <div className={styles.mobileHeroIdentity}>
                    <div className={styles.mobileHeroAvatar}>{getInitials(buyerCompany)}</div>
                    <div>
                      <p className={styles.mobileHeroTitle}>easyRFQ</p>
                      <p className={styles.mobileHeroSubtitle}>Supplier bid submission</p>
                    </div>
                  </div>
                  <span className={styles.statusPill}>{`${bidType.toLowerCase()} bid`}</span>
                </div>
              </div>

            <div className={styles.summaryCard}>
  <div className={styles.rfqHeroBlock}>
    <span className={styles.rfqHeroLabel}>RFQ ID</span>
    <h2 className={styles.rfqHeroCode}>{rfqContext?.rfq_code || "-"}</h2>
  </div>

  <div className={styles.rfqInfoGrid}>
    <div className={styles.rfqInfoItem}>
      <span>buyer company</span>
      <strong>{buyerCompany}</strong>
    </div>

    <div className={styles.rfqInfoItem}>
      <span>bid type</span>
      <strong>{bidType}</strong>
    </div>

    <div className={styles.rfqInfoItem}>
      <span>buyer email</span>
      <strong className={styles.emailValue}>{buyerEmail}</strong>
    </div>

    <div className={styles.rfqInfoItem}>
      <span>buyer mobile</span>
      <strong>{buyerMobile}</strong>
    </div>
  </div>
</div>

              <div className={styles.sectionBlock}>
                <div className={styles.sectionHeadingRow}>
                  <h2 className={styles.sectionHeading}>Requested Items</h2>
                  <span className={styles.sectionCount}>({rfqItems.length})</span>
                </div>

                <div className={styles.requestedItemsGrid}>
                  {rfqItems.length > 0 ? (
                    rfqItems.map((item, index) => (
                      <div
                        key={`${item?.itemCode || item?.itemName || "rfq-item"}-${index}`}
                        className={styles.requestedItemCard}
                      >
                        <div className={styles.requestedItemIdentity}>
                          <div className={styles.requestedItemAvatar}>
                            {getInitials(item?.itemName || item?.item_name || `I${index + 1}`)}
                          </div>
                          <div className={styles.requestedItemText}>
                            <strong>{item?.itemName || item?.item_name || "-"}</strong>
                            <span>Item code {item?.itemCode || item?.item_code || "No item code"}</span>
                          </div>
                        </div>

                        <button
                          type="button"
                          className={styles.specButton}
                          onClick={() =>
                            setDescriptionModal({
                              open: true,
                              title: item?.itemName || item?.item_name || "Item Description",
                              rows: buildItemSpecRows(item, {
                                rfqCode: rfqContext?.rfq_code,
                                buyerCompany,
                                buyerEmail,
                                buyerMobile,
                              }),
                              sections: [],
                            })
                          }
                        >
                          View Spec
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className={styles.emptySurface}>No RFQ items found.</div>
                  )}
                </div>
              </div>

              <div className={styles.softInfoCard}>
                <span className={styles.softCardLabel}>Shipping Details</span>
                <p className={styles.softCardValue}>{shippingAddress || "-"}</p>
              </div>

              <div className={styles.softInfoCard}>
                <div className={styles.bidWindowHeader}>
                  <h2 className={styles.sectionHeading}>Bidding Window</h2>
                  <span className={`${styles.statusPill} ${styles.warningPill}`}>{bidWindowLabel}</span>
                </div>

                <div className={styles.bidProgressTrack}>
                  <div
                    className={styles.bidProgressValue}
                    style={{
                      width: `${bidProgressPercent > 0 ? Math.max(10, bidProgressPercent) : 0}%`,
                    }}
                  ></div>
                </div>

                <div className={styles.bidWindowGrid}>
                  <div className={styles.summaryItem}>
                    <span>Start Date</span>
                    <strong>{bidStartMoment ? bidStartMoment.format("DD MMM YYYY, hh:mm A") : "-"}</strong>
                  </div>
                  <div className={styles.summaryItem}>
                    <span>End Date</span>
                    <strong>{bidEndMoment ? bidEndMoment.format("DD MMM YYYY, hh:mm A") : "-"}</strong>
                  </div>
                </div>
<div
  className={`${styles.mobileActionWrap} ${
    actionScrollState.left ? styles.showLeftFade : ""
  } ${actionScrollState.right ? styles.showRightFade : ""}`}
>
  <div ref={actionRowRef} className={styles.mobileActionRow}>
    <button
      type="button"
      className={`${styles.actionButton} ${styles.actionButtonSuccess}`}
      onClick={openTimelineModal}
    >
      Conversations
    </button>
    <button
      type="button"
      className={styles.actionButton}
      onClick={() => setMessageModalOpen(true)}
    >
      Message
    </button>
    <button
      type="button"
      className={styles.actionButton}
      onClick={() => setSubmitModalOpen(true)}
    >
      Upload Quote
    </button>
  </div>

  {actionScrollState.right ? (
    <p className={styles.scrollHint}>Scroll for more actions →</p>
  ) : null}
</div>
              </div>

              <div className={styles.sectionBlock}>
                <div className={styles.sectionHeadingRow}>
                  <h2 className={styles.sectionHeading}>Quotations Submitted</h2>
                  <span className={styles.sectionCount}>({quotationRows.length})</span>
                </div>

                <div className={styles.quotationCardList}>
                  {quotationRows.length > 0 ? (
                    quotationRows.map((row) => (
                      <div key={row.id} className={styles.quotationCard}>
                        <div className={styles.quotationCardHeader}>
                          <div className={styles.requestedItemIdentity}>
                            <div className={styles.requestedItemAvatar}>
                              {getInitials(
                                Array.isArray(row.itemNames) && row.itemNames.length > 1
                                  ? row.itemName
                                  : row.itemName || row.supplierName,
                              )}
                            </div>
                          <div className={styles.requestedItemText}>
                            <strong>
                              {row.lineItemCount > 1 ? "Multiple Items" : row.primaryItemName}
                            </strong>
                            <span>
                              {row.lineItemCount > 1
                                ? `${row.lineItemCount} line items • ${row.supplierName}`
                                : row.supplierName}
                            </span>
                          </div>
                        </div>

                          <div className={styles.quotationActionGroup}>
                             <button
                              type="button"
                              className={styles.specButton}
                              onClick={() =>
                                setDescriptionModal({
                                  open: true,
                                  title:
                                    row.lineItemCount > 1
                                      ? `Multiple Items (${row.lineItemCount})`
                                      : row.primaryItemName || "Quotation Details",
                                  rows: [],
                                  sections: buildQuotationSpecSections(row),
                                })
                              }
                            >
                              View Spec
                            </button>
                            {row.documentUrl ? (
                              <button
                                type="button"
                                className={styles.specButton}
                                onClick={() =>
                                  downloadFileFromUrl(
                                    row.documentUrl,
                                    row.documentName || "quotation.pdf",
                                  )
                                }
                                title={row.documentName || "Download file"}
                              >
                                Download
                              </button>
                            ) : row.manualPdfAvailable ? (
                              <button
                                type="button"
                                className={styles.specButton}
                                onClick={() => handleManualQuotationPdfDownload(row)}
                                title="Download quotation summary PDF"
                              >
                                Download
                              </button>
                            ) : null}
                           
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className={styles.emptySurface}>No bid submitted yet.</div>
                  )}
                </div>
              </div>

              <div className={styles.desktopOnly}>
                <div className={styles.inlineInfoBlock}>
                  <span className={styles.infoLabel}>Document</span>
                  <p className={styles.infoValue}>{rfqContext?.proposal_document ? "Available" : "-"}</p>
                </div>

                <div className={`${styles.tableWrap} ${styles.itemTableWrap}`}>
                  <table className={styles.detailTable}>
                    <thead>
                      <tr>
                        {[
                          "Item Name",
                          "Item Code",
                          "Expense Type",
                          "HSN Code",
                          "UNSPSC Code",
                          "Unit",
                          "Quantity",
                          "Description",
                        ].map((heading, headingIndex, headingList) => {
                          const compactClass =
                            heading === "Item Code"
                              ? styles.compactItemCodeCol
                              : heading === "Expense Type"
                                ? styles.compactExpenseCol
                                : heading === "HSN Code"
                                  ? styles.compactHsnCol
                                  : heading === "UNSPSC Code"
                                    ? styles.compactUnspscCol
                                    : heading === "Unit"
                                      ? styles.compactUnitCol
                                      : heading === "Quantity"
                                        ? styles.compactQuantityCol
                                        : heading === "Description"
                                          ? styles.compactDescriptionCol
                                          : "";

                          return (
                            <th
                              key={heading}
                              className={`${compactClass} ${
                                headingIndex === 0
                                  ? `${styles.mobileStickyStart} ${styles.compactItemNameCol}`
                                  : headingIndex === headingList.length - 1
                                    ? styles.mobileStickyEnd
                                    : ""
                              }`}
                            >
                              {heading}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {rfqItems.length > 0 ? (
                        rfqItems.map((item, index) => (
                          <tr key={`${item?.itemCode || item?.itemName || "rfq-item"}-${index}`}>
                            <td className={`${styles.mobileStickyStart} ${styles.compactItemNameCol}`}>
                              {item?.itemName || "-"}
                            </td>
                            <td className={styles.compactItemCodeCol}>{item?.itemCode || "-"}</td>
                            <td className={styles.compactExpenseCol}>{item?.expense_type || "-"}</td>
                            <td className={styles.compactHsnCol}>{item?.hsnCode || "-"}</td>
                            <td className={styles.compactUnspscCol}>{item?.unspscCode || "-"}</td>
                            <td className={styles.compactUnitCol}>{item?.unit_measure || item?.uom || item?.unit || "-"}</td>
                            <td className={styles.compactQuantityCol}>{formatQuantityWithUnit(item)}</td>
                            <td className={`${styles.mobileStickyEnd} ${styles.compactDescriptionCol}`}>
                              <button
                                type="button"
                                className={styles.linkButton}
                                onClick={() =>
                                  setDescriptionModal({
                                    open: true,
                                    title: item?.itemName || "Item Description",
                                    rows: buildItemSpecRows(item, {
                                      rfqCode: rfqContext?.rfq_code,
                                      buyerCompany,
                                      buyerEmail,
                                      buyerMobile,
                                    }),
                                    sections: [],
                                  })
                                }
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className={styles.emptyCell}>
                            No RFQ items found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className={`${styles.tableWrap} ${styles.supplierTableWrap}`}>
                  <table className={styles.bidTable}>
                    <thead>
                      <tr>
                        {[
                          "Supplier Name",
                          "Item Name",
                          "Quantity",
                          "Unit Price",
                          "Total Price",
                          "Submitted Date",
                          "File",
                        ].map((heading, headingIndex, headingList) => (
                          <th
                            key={heading}
                            className={
                              headingIndex === 0
                                ? `${styles.mobileStickyStart} ${styles.supplierNameCol}`
                                : headingIndex === headingList.length - 1
                                  ? styles.mobileStickyEnd
                                  : ""
                            }
                          >
                            {heading}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {quotationRows.length > 0 ? (
                        quotationRows.map((row) => (
                          <tr key={row.id}>
                            <td className={`${styles.mobileStickyStart} ${styles.supplierNameCol}`}>
                              {row.supplierName}
                            </td>
                            <td>
                              {renderSummaryLink(
                                Array.isArray(row.itemNames) && row.itemNames.length > 1 ? "Multiple Items" : row.itemName,
                                row.itemNames,
                                "item",
                              )}
                            </td>
                            <td>
                              {renderSummaryLink(
                                Array.isArray(row.quantityLines) && row.quantityLines.length > 1
                                  ? "Multiple Qty"
                                  : row.quantity,
                                row.quantityLines,
                                "quantity",
                              )}
                            </td>
                            <td>{row.unitPrice}</td>
                            <td>{row.totalPrice}</td>
                            <td>{row.submittedAt}</td>
                            <td className={styles.mobileStickyEnd}>
                              {row.documentUrl ? (
                                <button
                                  type="button"
                                  className={styles.linkButton}
                                  onClick={() =>
                                    downloadFileFromUrl(
                                      row.documentUrl,
                                      row.documentName || "quotation.pdf",
                                    )
                                  }
                                  title={row.documentName || "Download file"}
                                >
                                  <span>{row.documentName || "Download"}</span>
                                </button>
                              ) : row.manualPdfAvailable ? (
                                <button
                                  type="button"
                                  className={styles.linkButton}
                                  onClick={() => handleManualQuotationPdfDownload(row)}
                                  title="Download quotation summary PDF"
                                >
                                  <span>{row.documentName || "quotation-summary.pdf"}</span>
                                </button>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className={styles.emptyCell}>
                            No bid submitted yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className={styles.tableFooter}>
                  <span>Total count</span>
                  <div className={styles.footerBox}>{quotationRows.length}</div>
                  <span>|</span>
                  <span>Page</span>
                  <div className={styles.footerBox}>0</div>
                </div>
              </div>
            </>
          )}
        </section>
      </main>

      {activeTooltip ? (
        <div
          className={styles.floatingTooltip}
          style={{
            top: `${activeTooltip.top}px`,
            left: `${Math.min(activeTooltip.left, window.innerWidth - 360)}px`,
          }}
          onMouseEnter={() => setActiveTooltip((prev) => prev)}
          onMouseLeave={closeSummaryTooltip}
        >
          {activeTooltip.entries.map((entry, index) => (
            <div key={`${activeTooltip.type}-${index}`} className={styles.hoverPopoverRow}>
              {activeTooltip.type === "quantity" ? `${entry.itemName}: ${entry.quantity}` : entry}
            </div>
          ))}
        </div>
      ) : null}

      <Modal open={messageModalOpen} onClose={resetMessageModal}>
        <Fade in={messageModalOpen}>
          <div className={styles.modalCard}>
            <h3 className={styles.modalHeading}>Message</h3>
            <p className={styles.modalSubheading}>Please enter your message</p>

            <textarea
              className={styles.messageTextarea}
              value={messageText}
              onChange={(event) => setMessageText(event.target.value.slice(0, 2000))}
              placeholder="Type your message here..."
              rows={8}
            />

            {sendMessageError ? <p className={styles.errorText}>{sendMessageError}</p> : null}

            <div className={styles.attachBlock}>
              <div className={styles.attachActions}>
                <label htmlFor="vendor-message-file" className={styles.attachTrigger}>
                  <AttachmentOutlined sx={{ fontSize: "22px" }} />
                  <span>Attach a file</span>
                </label>
              </div>
              <input
                id="vendor-message-file"
                type="file"
                className={styles.hiddenInput}
                accept=".pdf,.jpeg,.jpg,.png,.xls,.xlsx,.doc,.docx,.xml,.csv,image/*"
                onChange={handleMessageFileChange}
              />

              {messageFiles.length > 0 ? (
                <div className={styles.fileList}>
                  {messageFiles.map((file, index) => (
                    <div key={`${file.name}-${index}`} className={styles.fileChip}>
                      <span>{file.name}</span>
                      <button
                        type="button"
                        onClick={() => setMessageFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index))}
                      >
                        <DeleteOutline fontSize="small" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <div className={styles.modalActions}>
              <button type="button" className={styles.cancelButton} onClick={resetMessageModal}>
                Cancel
              </button>
              <button type="button" className={styles.primaryButton} onClick={handleSendMessage} disabled={sendingMessage}>
                {sendingMessage ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </Fade>
      </Modal>

      <Modal open={submitModalOpen} onClose={resetSubmitModal}>
        <Fade in={submitModalOpen}>
          <div className={styles.modalCardLarge}>
            <h3 className={styles.modalHeading}>Upload Quotation</h3>
            <p className={styles.modalSubheading}>Upload proposal document</p>

            <div className={styles.radioRow}>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="proposal-mode"
                  value="yes"
                  checked={proposalMode === "yes"}
                  onChange={() => setProposalMode("yes")}
                />
                <span className={styles.radioIndicator} aria-hidden="true"></span>
                <span>Yes</span>
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="proposal-mode"
                  value="no"
                  checked={proposalMode === "no"}
                  onChange={() => setProposalMode("no")}
                />
                <span className={styles.radioIndicator} aria-hidden="true"></span>
                <span>No</span>
              </label>
            </div>

            <div className={styles.currencyRow}>
              <SelectCountry
                value={fromCurrency}
                setValue={setFromCurrency}
                label="Currency"
              />
            </div>

            {proposalMode === "yes" ? (
              <div className={styles.uploadBox}>
                <div>
                  <p className={styles.uploadTitle}>Drag and drop file here</p>
                  <p className={styles.uploadHint}>(Supported formats: PDF, image, Excel, Word, XML, CSV)</p>
                  {proposalFile ? <p className={styles.selectedFile}>{proposalFile.name}</p> : null}
                </div>
                <div className={styles.uploadActions}>
                  <label htmlFor="proposal-file" className={styles.browseButton}>
                    Browse
                  </label>
                  <label htmlFor="proposal-camera-file" className={styles.browseButton}>
                    <CameraAltOutlined sx={{ fontSize: "18px" }} />
                    Capture Image
                  </label>
                  <button
                    type="button"
                    className={styles.browseButton}
                    onClick={openCameraModal}
                  >
                    <CameraAltOutlined sx={{ fontSize: "18px" }} />
                    Open Camera
                  </button>
                </div>
                <input
                  id="proposal-file"
                  type="file"
                  className={styles.hiddenInput}
                  accept=".pdf,.jpeg,.jpg,.png,.xls,.xlsx,.doc,.docx,.xml,.csv"
                  onChange={handleBidFileChange}
                />
                <input
                  id="proposal-camera-file"
                  type="file"
                  className={styles.hiddenInput}
                  accept="image/*"
                  capture="environment"
                  onChange={handleBidFileChange}
                />
              </div>
            ) : (
              <div className={styles.manualBidList}>
                {manualBidRows.map((row, index) => (
                  <div key={`${row.itemCode || row.itemName || "manual-item"}-${index}`} className={styles.manualBidCard}>
                    <label className={styles.fieldGroup}>
                      <span>Item Name</span>
                      <input value={row.itemName} readOnly />
                    </label>

                    <div className={styles.fieldRow}>
                      <label className={styles.fieldGroup}>
                        <span>Quantity</span>
                        <input
                          value={row.quantity}
                          onChange={(event) => handleManualBidChange(index, "quantity", event.target.value)}
                          placeholder="Enter Quantity"
                        />
                      </label>
                      <label className={styles.fieldGroup}>
                        <span>Unit Price</span>
                        <input
                          value={row.unitPrice}
                          onChange={(event) => handleManualBidChange(index, "unitPrice", event.target.value)}
                          placeholder="Enter Unit Price"
                        />
                      </label>
                    </div>

                    <label className={styles.fieldGroup}>
                      <span>Proposal Description</span>
                      <textarea
                        rows={5}
                        value={row.proposalDescription}
                        onChange={(event) => handleManualBidChange(index, "proposalDescription", event.target.value)}
                        placeholder="Enter Proposal Description"
                      />
                    </label>
                  </div>
                ))}
              </div>
            )}

            {submitBidError ? <p className={styles.errorText}>{submitBidError}</p> : null}

            <div className={styles.modalActions}>
              <button type="button" className={styles.cancelButton} onClick={resetSubmitModal}>
                Cancel
              </button>
              <button type="button" className={styles.primaryButton} onClick={handleSubmitBid} disabled={submittingBid}>
                {submittingBid ? "Submitting..." : "Upload Quotation"}
              </button>
            </div>
          </div>
        </Fade>
      </Modal>

      <Modal open={timelineModalOpen} onClose={() => setTimelineModalOpen(false)}>
        <Fade in={timelineModalOpen}>
          <div className={styles.timelineModal}>
            <div className={styles.timelineHeaderBlock}>
              <h3 className={styles.modalHeading}>RFQ Transaction Details</h3>
              <div className={styles.timelineSummaryGrid}>
                {transactionDetails.map(([label, value]) => (
                  <div key={label} className={styles.topInfoItem}>
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.timelineContentWrap}>
              {timelineLoading ? (
                <Typography sx={{ fontFamily: "gilroyMedium", fontSize: "14px" }}>
                  Loading vendor chat...
                </Typography>
              ) : timelineError ? (
                <div className={styles.emptyTimeline}>{timelineError}</div>
              ) : (
                renderTimelineCards(timelineData)
              )}

              <div className={styles.uploadedDocsBlock}>
                <h4>Uploaded Documents</h4>
                {timelineDocuments.length > 0 ? (
                  <div className={styles.uploadedDocsList}>
                    {timelineDocuments.map((entry, index) => {
                      const file = entry?.document?.[0];
                      return file?.url ? (
                        <a
                          key={`${file.url}-${index}`}
                          href={file.url}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.timelineAttachment}
                        >
                          {file.name || `Attachment ${index + 1}`}
                        </a>
                      ) : null;
                    })}
                  </div>
                ) : (
                  <div className={styles.emptyTimeline}>No uploaded documents found.</div>
                )}
              </div>
            </div>

            <div className={styles.modalActions}>
              <button type="button" className={styles.primaryButton} onClick={() => setTimelineModalOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </Fade>
      </Modal>

      <Modal
  open={descriptionModal.open}
  onClose={() => setDescriptionModal({ open: false, title: "", rows: [], sections: [] })}
>
  <Fade in={descriptionModal.open}>
    <div className={`${styles.modalCard} ${styles.specModalCard}`}>
      <h3 className={styles.modalHeading}>{descriptionModal.title}</h3>

      <div className={styles.specModalBody}>
        {Array.isArray(descriptionModal.sections) && descriptionModal.sections.length > 0 ? (
          descriptionModal.sections.map((section, sectionIndex) => (
            <div key={`${section.title}-${sectionIndex}`} className={styles.specSection}>
              <div className={styles.specSectionTitle}>{section.title}</div>
              {Array.isArray(section.tableHeaders) && Array.isArray(section.tableRows) ? (
                <table className={`${styles.specTable} ${styles.itemSpecTable}`}>
                  <thead>
                    <tr>
                      {section.tableHeaders.map((heading) => (
                        <th key={heading}>{heading}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.tableRows.map((row, rowIndex) => (
                      <tr key={`${section.title}-item-${rowIndex}`}>
                        {row.map((value, cellIndex) => {
                          const heading = section.tableHeaders[cellIndex];
                          const textValue = String(value ?? "-");
                          const isNameCell = heading === "Actual Name" || heading === "Item Name";
                          const isDescriptionCell = heading === "Description";

                          return (
                            <td key={`${section.title}-item-${rowIndex}-${cellIndex}`}>
                              {isDescriptionCell ? (
                                <Tooltip
                                  title={textValue}
                                  arrow
                                  placement="top"
                                  enterDelay={250}
                                  componentsProps={specNameTooltipProps}
                                >
                                  <span>
                                    <ReadMoreText value={value} />
                                  </span>
                                </Tooltip>
                              ) : isNameCell ? (
                                <Tooltip
                                  title={textValue}
                                  arrow
                                  placement="top"
                                  enterDelay={250}
                                  componentsProps={specNameTooltipProps}
                                >
                                  <span className={styles.specEllipsisText}>{textValue}</span>
                                </Tooltip>
                              ) : (
                                value ?? "-"
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className={styles.specTable}>
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(section.rows) ? section.rows : []).map(([label, value], rowIndex) => (
                      <tr key={`${section.title}-${label}-${rowIndex}`}>
                        <td>{label}</td>
                        <td>{label === "Description" ? <ReadMoreText value={value} /> : value ?? "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))
        ) : (
          <table className={styles.specTable}>
            <thead>
              <tr>
                <th>Field</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(descriptionModal.rows) ? descriptionModal.rows : []).map(
                ([label, value], index) => (
                  <tr key={`${label}-${index}`}>
                    <td>{label}</td>
                    <td>{label === "Description" || label === "Specifications" ? <ReadMoreText value={value} /> : value ?? "-"}</td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.specModalFooter}>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={() => setDescriptionModal({ open: false, title: "", rows: [], sections: [] })}
        >
          Close
        </button>
      </div>
    </div>
  </Fade>
</Modal>
      <Modal open={cameraModalOpen} onClose={closeCameraModal}>
        <Fade in={cameraModalOpen}>
          <div className={styles.modalCard}>
            <h3 className={styles.modalHeading}>Capture Image</h3>
            <p className={styles.modalSubheading}>Use your device camera to upload the quotation image.</p>

            <div className={styles.cameraStage}>
              {cameraLoading ? <div className={styles.emptyTimeline}>Opening camera...</div> : null}
              {cameraError ? <div className={styles.errorText}>{cameraError}</div> : null}
              <video ref={videoRef} className={styles.cameraVideo} autoPlay playsInline muted />
              <canvas ref={canvasRef} className={styles.hiddenCanvas} />
            </div>

            <div className={styles.modalActions}>
              <button type="button" className={styles.cancelButton} onClick={closeCameraModal}>
                Cancel
              </button>
              <button type="button" className={styles.primaryButton} onClick={capturePhoto} disabled={cameraLoading}>
                Capture
              </button>
            </div>
          </div>
        </Fade>
      </Modal>

      <Modal open={infoPopup.open} onClose={closeInfoPopup}>
        <Fade in={infoPopup.open}>
          <div className={styles.infoModal}>
            <Typography sx={{ fontFamily: "gilroyBold", fontSize: 18, mb: 1 }}>
              {infoPopup.title}
            </Typography>
            <Typography sx={{ fontFamily: "gilroyRegular", fontSize: 14, color: "#475569", mb: 2 }}>
              {infoPopup.description}
            </Typography>
            <button type="button" className={styles.primaryButton} onClick={closeInfoPopup}>
              Okay
            </button>
          </div>
        </Fade>
      </Modal>

      <Modal
        open={responseModal.open}
        onClose={() => setResponseModal((prev) => ({ ...prev, open: false }))}
        closeAfterTransition
      >
        <Fade in={responseModal.open}>
          <div className={styles.responseModal}>
            <SuccessPopupDashboard
              popupImage={successPopupImg}
              popupHeroText={responseModal.title}
              popupDescriptionLG={responseModal.message}
              popBtn={true}
              vendorBtn={true}
              btnText="Okay"
              btnClick={() => setResponseModal((prev) => ({ ...prev, open: false }))}
            />
          </div>
        </Fade>
      </Modal>
    </div>
  );
};

export default FreeRFQDocumentExtraction;
